import React, { useEffect, useMemo, useState } from "react";
import { useMastery } from "../context/MasteryContext";
import { useAuth } from "../context/AuthContext";
import { useT } from "../context/LanguageContext";
import { shareTrophyImage } from "../lib/shareTrophy";
import { Trophy, Share2 } from "lucide-react";
import { toast } from "sonner";

/** Generate a random but stable array of confetti pieces each time the overlay opens. */
function useConfetti(active, n = 80) {
  return useMemo(() => {
    if (!active) return [];
    const colors = ["#FFB703", "#06D6A0", "#118AB2", "#EF476F", "#9333EA", "#FFD166", "#00B4D8"];
    return Array.from({ length: n }, (_, i) => ({
      id: i,
      left: Math.random() * 100,           // %
      delay: Math.random() * 1.2,          // s
      duration: 2.4 + Math.random() * 2.2, // s
      drift: (Math.random() - 0.5) * 240,  // px horizontal drift
      size: 8 + Math.random() * 10,        // px
      rotate: Math.random() * 720 + 360,   // degrees end
      color: colors[i % colors.length],
      shape: Math.random() > 0.5 ? "rect" : "circle",
    }));
  }, [active, n]);
}

export default function Celebration() {
  const { celebration, dismiss, mastery } = useMastery() || {};
  const { user } = useAuth();
  const tr = useT();
  const active = !!celebration;
  const isMilestone = !!celebration?.milestone;
  const pieces = useConfetti(active, isMilestone ? 180 : 80);
  const [visible, setVisible] = useState(false);
  const [sharing, setSharing] = useState(false);

  useEffect(() => {
    if (!active) { setVisible(false); return; }
    const t = setTimeout(() => setVisible(true), 20);
    const auto = setTimeout(() => dismiss?.(), isMilestone ? 7000 : 4500);
    return () => { clearTimeout(t); clearTimeout(auto); };
  }, [active, dismiss, isMilestone]);

  if (!active) return null;

  const handleShare = async (e) => {
    e.stopPropagation();
    if (sharing) return;
    setSharing(true);
    try {
      const res = await shareTrophyImage({
        userName: user?.name || "",
        total: mastery?.total ?? (celebration.milestone || 1),
        itemTitle: celebration.title,
        milestone: celebration.milestone,
      });
      if (res.method === "download") {
        toast.success(tr("Trophy image saved!"), {
          description: tr("Now share it on Instagram or Facebook 🎉"),
          duration: 6000,
        });
      } else if (res.method === "cancelled") {
        // user bailed; no toast
      } else if (res.method === "native-share") {
        toast.success(tr("Thanks for sharing! 🎉"));
      }
    } catch (err) {
      toast.error(tr("Couldn't generate the image — try again?"));
    } finally {
      setSharing(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center"
      role="dialog"
      aria-label={tr("Mastery celebration")}
      data-testid="celebration-overlay"
      onClick={dismiss}
    >
      {/* backdrop */}
      <div
        className="absolute inset-0 transition-opacity"
        style={{
          background: isMilestone
            ? "radial-gradient(ellipse at center, rgba(255,209,102,0.55) 0%, rgba(0,0,0,0.75) 75%)"
            : "radial-gradient(ellipse at center, rgba(255,183,3,0.35) 0%, rgba(0,0,0,0.55) 70%)",
          opacity: visible ? 1 : 0,
          transitionDuration: "350ms",
        }}
      />

      {/* rays behind the trophy */}
      <svg
        viewBox="0 0 400 400"
        className="absolute pointer-events-none"
        style={{
          width: isMilestone ? "160vmin" : "120vmin",
          height: isMilestone ? "160vmin" : "120vmin",
          animation: `trophy-spin ${isMilestone ? "5s" : "8s"} linear infinite`,
          opacity: visible ? (isMilestone ? 0.8 : 0.55) : 0,
          transition: "opacity 500ms ease",
        }}
        aria-hidden
      >
        {Array.from({ length: isMilestone ? 48 : 24 }).map((_, i) => (
          <rect
            key={i}
            x={199}
            y={0}
            width={isMilestone ? 3 : 2}
            height={200}
            fill={isMilestone ? "#FFE28A" : "#FFD166"}
            transform={`rotate(${i * (360 / (isMilestone ? 48 : 24))} 200 200)`}
          />
        ))}
      </svg>

      {/* Confetti */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {pieces.map((p) => (
          <span
            key={p.id}
            className="absolute top-[-10%]"
            style={{
              left: `${p.left}%`,
              width: `${p.size}px`,
              height: `${p.size * (p.shape === "rect" ? 0.45 : 1)}px`,
              background: p.color,
              borderRadius: p.shape === "circle" ? "50%" : "2px",
              animation: `confetti-fall ${p.duration}s ${p.delay}s linear forwards`,
              ["--drift"]: `${p.drift}px`,
              ["--rotate"]: `${p.rotate}deg`,
              transform: "translate3d(0,0,0)",
              boxShadow: "0 0 6px rgba(0,0,0,0.08)",
            }}
          />
        ))}
      </div>

      {/* Milestone banner — only on milestones */}
      {isMilestone && (
        <div
          className="absolute top-[10%] sm:top-[12%] z-10 px-5 py-2 rounded-full font-display font-black text-sm sm:text-base tracking-widest"
          style={{
            background: "linear-gradient(90deg, #FFB703, #FFD166, #FFB703)",
            color: "#3d2a00",
            border: "2px solid #8b5a00",
            boxShadow: "0 10px 30px rgba(0,0,0,0.35)",
            transform: visible ? "translateY(0) scale(1)" : "translateY(-40px) scale(0.8)",
            opacity: visible ? 1 : 0,
            transition: "all 600ms cubic-bezier(0.2, 1.4, 0.4, 1)",
          }}
          data-testid="milestone-banner"
        >
          🏆 {celebration.milestone} {tr("MASTERED · MILESTONE UNLOCKED")} 🏆
        </div>
      )}

      {/* Trophy + text */}
      <div
        className="relative z-10 flex flex-col items-center text-center px-6"
        style={{
          transform: visible ? "scale(1)" : "scale(0.2)",
          opacity: visible ? 1 : 0,
          transition: "transform 550ms cubic-bezier(0.2, 1.4, 0.4, 1), opacity 300ms ease",
        }}
      >
        <div
          className={`rounded-full flex items-center justify-center relative ${isMilestone ? "w-56 h-56 sm:w-72 sm:h-72" : "w-40 h-40 sm:w-56 sm:h-56"}`}
          style={{
            background: "radial-gradient(circle at 30% 20%, #FFF3B0, #FFB703 55%, #B77800 100%)",
            boxShadow: isMilestone
              ? "0 0 80px 16px rgba(255,209,102,0.9), inset 0 0 40px rgba(255,255,255,0.65)"
              : "0 0 60px 10px rgba(255,183,3,0.7), inset 0 0 30px rgba(255,255,255,0.55)",
            animation: isMilestone ? "trophy-pulse 1.4s ease-in-out infinite" : "none",
          }}
        >
          <Trophy className={isMilestone ? "w-32 h-32 sm:w-44 sm:h-44" : "w-24 h-24 sm:w-32 sm:h-32"} style={{ color: "#3d2a00" }} strokeWidth={2.4} />
          <span
            className="absolute top-4 left-6 w-10 h-4 rounded-full"
            style={{ background: "rgba(255,255,255,0.85)", filter: "blur(1px)", transform: "rotate(-20deg)" }}
          />
        </div>

        <div
          className={`font-play drop-shadow-xl ${isMilestone ? "text-6xl sm:text-8xl" : "text-5xl sm:text-7xl"} mt-5`}
          style={{ color: "#FFE28A", textShadow: "0 3px 0 #8b5a00, 0 0 24px rgba(255,183,3,0.7)" }}
          data-testid="celebration-title"
        >
          {isMilestone ? tr("INCREDIBLE!") : tr("HOORAY!")}
        </div>
        <div className="font-display text-xl sm:text-3xl font-bold mt-2 text-white" data-testid="celebration-item">
          {celebration.title}
        </div>
        <div className={`font-play mt-1 ${isMilestone ? "text-3xl sm:text-5xl" : "text-2xl sm:text-3xl"}`} style={{ color: "#FFE28A" }}>
          {celebration.subtitle}
        </div>
        <div className="flex flex-col sm:flex-row gap-3 mt-6">
          <button
            onClick={handleShare}
            disabled={sharing}
            data-testid="celebration-share"
            className="px-6 py-3 rounded-full font-bold text-sm transition-transform hover:scale-105 inline-flex items-center justify-center gap-2"
            style={{ background: "#3d2a00", color: "#FFE28A", boxShadow: "0 6px 16px rgba(0,0,0,0.35)" }}
          >
            <Share2 className="w-4 h-4" /> {sharing ? tr("Creating…") : tr("Share my trophy")}
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); dismiss(); }}
            data-testid="celebration-close"
            className="px-6 py-3 rounded-full font-bold text-sm transition-transform hover:scale-105"
            style={{ background: "#fff", color: "#3d2a00", boxShadow: "0 6px 16px rgba(0,0,0,0.25)" }}
          >
            {isMilestone ? tr("Keep going! 🎉") : tr("Keep going! 🎉")}
          </button>
        </div>
      </div>

      {/* Scoped keyframes */}
      <style>{`
        @keyframes trophy-spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        @keyframes trophy-pulse {
          0%, 100% { transform: scale(1); }
          50%      { transform: scale(1.06); }
        }
        @keyframes confetti-fall {
          0%   { transform: translate3d(0, 0, 0) rotate(0deg); opacity: 1; }
          100% { transform: translate3d(var(--drift), 110vh, 0) rotate(var(--rotate)); opacity: 0.9; }
        }
      `}</style>
    </div>
  );
}
